import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import LowPriorityIcon from '@mui/icons-material/LowPriority'
import {
  IconButton,
  InputAdornment,
  Autocomplete as MuiAutocomplete,
  Stack,
  TextField,
} from '@mui/material'
import PropTypes from 'prop-types'
import { ConfirmableButton, Spinner } from '~/components'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'

const Autocomplete = (props) => {
  const { label, onCreate, onChange, ...rest } = props
  const [value, setValue] = useState()
  return (
    <MuiAutocomplete
      {...rest}
      disabled={!!value}
      freeSolo
      getOptionLabel={(option) => option?.name ?? ''}
      loading
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            ...(value
              ? {
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: 0.5 }}>
                      <IconButton edge="end">
                        <Spinner width="1em" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              : {}),
          }}
          label={value || label}
        />
      )}
      sx={{ flexGrow: 1 }}
      onChange={async (_, newValue, reason) => {
        switch (reason) {
          case 'createOption':
            setValue(newValue)
            await onCreate(newValue)
            setValue()
            break
          case 'selectOption':
            onChange(newValue)
            break
        }
      }}
    />
  )
}

Autocomplete.propTypes = {
  label: PropTypes.string,
  onCreate: PropTypes.func,
  onChange: PropTypes.func,
}

const MoveButton = (props) => {
  const { icon = <LowPriorityIcon />, product, ...rest } = props

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const route = useRouteContext(MoveButton.name)
  const [categoryId, setCategoryId] = useState(route.category?.id)
  const category = useSelector((state) =>
    bshSlice.categoryById(state, categoryId)
  )
  const [subCategoryId, setSubCategoryId] = useState(null)
  const subCategory = useSelector((state) =>
    bshSlice.categoryById(state, subCategoryId)
  )
  const categories = useSelector((state) =>
    bshSlice.categories(state, route?.brand?.categories)
  )
  const subCategories = useSelector((state) =>
    bshSlice.categories(state, category?.categories)
  )
  const filteredSubCategories = useMemo(() => {
    return subCategories.filter((c) => c.id !== product?.category_id)
  }, [subCategories, product?.category_id])

  useEffect(() => {
    setSubCategoryId(null)
  }, [category])

  return (
    <ConfirmableButton
      {...rest}
      dialog={{
        actions: {
          confirm: {
            disabled: !category || !subCategory,
            name: t('ui.move'),
          },
        },
        body: () => (
          <Stack spacing={2}>
            <Autocomplete
              label={t('product.to-group')}
              loadingText={t('category.enter-to-create-group')}
              options={categories}
              value={category ?? ''}
              onChange={(value) => setCategoryId(value?.id)}
              onCreate={async (name) => {
                setCategoryId(null)
                const result = await dispatch(
                  bshSlice.createCategory({
                    data: { brand: route?.brand, name },
                  })
                )
                setCategoryId(result.payload?.id ?? null)
              }}
            />
            <Autocomplete
              label={t('product.to-category')}
              loadingText={t('category.enter-to-create-category')}
              options={filteredSubCategories}
              value={subCategory ?? ''}
              onChange={(value) => setSubCategoryId(value?.id)}
              onCreate={async (name) => {
                setSubCategoryId(null)
                const result = await dispatch(
                  bshSlice.createCategory({
                    data: { brand: route?.brand, parent_id: category.id, name },
                  })
                )
                setSubCategoryId(result.payload?.id ?? null)
              }}
            />
          </Stack>
        ),
        maxWidth: 'sm',
        title: t('product.move'),
      }}
      icon={icon}
      onConfirmed={async () => {
        await dispatch(
          bshSlice.moveProduct({ data: { product, category: subCategory } })
        )
        return true
      }}
    />
  )
}

MoveButton.propTypes = {
  icon: PropTypes.element,
  product: PropTypes.object.isRequired,
}

export default MoveButton
